import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import {
  copyTextToClipboard,
  defaultCellRenderer,
  getOriginalRecord,
  handleSocketChangesOnGridStore,
  showConfirmationDialog
} from "../../../other/utils/utils.jsx";
import {cellMenuItemCopyContactToClipboard, cellMenuItemRemoveRow} from "../../../other/utils/gridCellMenuUtils.jsx";
import {CONTACT_VIEW_COLUMNS} from "../../../other/constants/ContactViewColumns.jsx";
import {contactGridRowExpander, CONTACTS_GRID_CONFIG} from "../../../other/constants/ContactViewGridConfig.jsx";
import { assignViewsState, callOnLoadOnColumns, canOpenEditor, debounce } from "../../../other/utils/otherUtils.jsx";
import {EDITOR_KEYS} from "../../../other/constants/EditorKeys.jsx";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import {updateTabTitleWithRecordCount} from "../../../other/utils/editorUtils.jsx";
import { MessageDialog, Store } from "@bryntum/schedulerpro";
import { SWIVU_ASSIST_CONFIG } from "../../../other/constants/OtherKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

const tabTitle = "Contacts";

const entityContactColumns = CONTACT_VIEW_COLUMNS.map((contactColumn) => {
  return {...contactColumn, field: `contactRecord.${contactColumn.field}`};
});

const entityContactRowExpander = { ...contactGridRowExpander, dataField: `contactRecord.${contactGridRowExpander.dataField}` };

export const HasContactBaseMixin = (base) => (
  class extends base {
    static get $name() {
      return "HasContactBaseMixin";
    }

    get swivuDeepLinkSlug() {
      return "contact";
    }
    static get configurable() {
      return {
        title: tabTitle,
        tab : {
          icon: "fa-regular fa-user w-4 h-4",
        },
        items: {
          contactGrid: {
            height: "100%",
            type: WIDGET_KEYS.swivuModernGrid,
            swivuGridTitle: "Contacts",
            columns: entityContactColumns,
            features: {
              rowExpander: entityContactRowExpander,
              rowReorder: { showGrip: true, disabled: true },
              cellMenu: {
                items: {
                  cut: false,
                  copy: false,
                  paste: false,
                  copyToClipboard: cellMenuItemCopyContactToClipboard({
                    onItem: "up.onCopyContact"
                  }),
                  removeRow: cellMenuItemRemoveRow({
                    onItem: "up.onDeleteFromList",
                    icon: "fa-regular fa-trash !text-danger"
                  }),
                }
              },
            },
            listeners: {
              cellClick: "up.onClickEntity",
            },
            tbar: {
              items: {
                addButton: {
                  onClick: "up.onAddContact",
                  cls: 'swivu-modern-button b-blue b-raised',
                  text: "Add Contact",
                  icon: "b-fa b-fa-plus b-button-icon",
                },
              }
            }
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.contact
      }
    }

    loadData() {
      const { contactGrid } = this.widgetMap;
      const entityContacts = this.getStoreData();
      if (entityContacts) {
        /* We are assigning new store because `updateStore` function is not firing in swivuAssitanceMixin which was causing issues in activating swivu assistance in this grid */
        contactGrid.store = new Store({
          data: entityContacts.sort((a, b) => a.order - b.order)
        });
      }
    }

    loadRecord(record, context, otherParams) {
      super.loadRecord(record, context, otherParams);

      const { contactGrid } = this.widgetMap;
      contactGrid.stateId = this.stateId;

      assignViewsState([contactGrid], this.stateProvider);
      callOnLoadOnColumns(contactGrid.columns, context.project, { editorHandler: context.editorHandler, editorNameFieldPrefix: `contactRecord.` });

      this.loadData();
      this.swivuContactGridUpdateListener = updateTabTitleWithRecordCount(this, contactGrid.store, tabTitle);

      this.debouncedCellClick =  debounce(({ column, record, event }) => {
        const originalRecord = getOriginalRecord(record)
        if (canOpenEditor(column, originalRecord) && event.detail === 1 && column.type === WIDGET_KEYS.columnContactStatus || column.type === WIDGET_KEYS.columnContactCategory || column.type === WIDGET_KEYS.columnContactTag) {
          context.editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
            entityRecord: originalRecord,
            event,
            column
          }, { by: "recordType", useExisting: true });
        }
      }, 200);
      handleSocketChangesOnGridStore(contactGrid, this.getStore(), this.getStoreData.bind(this));
    }

    onAddContact() {
      const newRecord = this.project.contactsStore.createRecord();
      this.editorHandler.openEditor(EDITOR_KEYS.contactFinder, newRecord, {
        contactRecord: newRecord,
        selectedEntities: {
          [this.getEntityStoreKey()]: [this.record]
        }
      }, {
        by: "recordType", useExisting: true,
      });
    }

    onDeleteFromList() {
      const { contactGrid } = this.widgetMap;
      const contactsToRemoveFromList = contactGrid.selectedRecords;
      if (contactsToRemoveFromList.length) {
        // prevents user self contact record to be removed from the contact list
        if (this.record.selfContactId === contactsToRemoveFromList[0].contactRecord.id) {
          MessageDialog.alert({
            title: "Cannot Remove User's Self Contact",
            message: "This is the user's self contact record and it cannot be removed from the contact list."
          });
        } else {
          const contactNames = contactsToRemoveFromList.map((entityContact) => defaultCellRenderer({ value: entityContact.contactRecord.name })).join("<br/>");
          showConfirmationDialog({
            title: "Delete Contact(s) From List",
            message: `<div>Are you sure you want to delete the following contact(s) from the list?<br/><br/>${contactNames}</div>`
          }).then((isDeleteFromList) => {
            if (isDeleteFromList) {
              this.getStore().remove(contactsToRemoveFromList);
              contactGrid.store.remove(contactsToRemoveFromList);
            }
          });
        }
      }
    }

    onClickEntity({event, column, record}) {
      const { id, type } = event.target.dataset;
      if (column.id === id && type === 'iconType') {
        copyTextToClipboard(record.contactRecord[id], `Copied ${id} to clipboard`);
      }
      if (column.id === 'entities') {
        const { id, type } = event.target.dataset;
        if (type === "Task") {
          const task = this.project.extraTaskStore.getById(id);
          this.context.entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: task.id });
        } else if (type === "Resource") {
          const resource = this.project.resourceStore.getById(id);
          this.editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, resource, {resource: resource});
        } else if (type === "Client") {
          const client = this.project.clientsStore.getById(id);
          this.editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, client, {client: client});
        } else if (type === "User") {
          const user = this.project.boardUsersStore.getById(id);
          this.editorHandler.openEditor(EDITOR_KEYS.userEditor, user, {user: user})
        }
      } else {
        const contactRecord = record.contactRecord
        this.debouncedCellClick({ column, record: contactRecord, event });
      }
    }

    onCopyContact(e) {
      e.selection = e.selection.map((entityContact) => entityContact.contactRecord);
      CONTACTS_GRID_CONFIG.cellMenuFeature.items.copyToClipboard.onItem(e);
    }

    onClickSwivuEditMessage({ tabName }) {
      this.editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
    }

    swivuCheckGrants(user) {
      if (!user.can(GRANTS.grant_four)) {
        this.readOnly = true;
      }
    }

    onSwivuEditorHide() {
      super.onSwivuEditorHide();
      if (this.swivuContactGridUpdateListener) {
        this.swivuContactGridUpdateListener();
      }
    }
  }
)
